// Thành phần dùng chung của hồ sơ 360: huy hiệu hạng / phân khúc, khung mục, thanh liên kết hồ sơ bán hàng
import React, { useMemo, useState } from 'react';
import { Input, Modal, Tooltip, message } from 'antd';
import { Crown, Link2, Search, Sparkles, Unlink } from 'lucide-react';
import usePosStore from '../POS/store';
import { formatPhone } from '../POS/utils';
import { useTreatmentAuth } from '../Treatment/actions';
import type { SegmentKey, Tier } from './data';
import { SEGMENT } from './logic';
import useC360Store from './store';
import type { C360 } from './useCustomer360';
import type { SignTarget } from './SignModal';
import { DEMO_ENABLED, clearDemo360, seedDemo360 } from './demo';

export interface C360Ops {
  goTab: (key: string) => void;
  pay: (cardId: string) => void;
  deduct: (cardId: string) => void;
  book: (cardId: string) => void;
  sign: (t: SignTarget) => void;
  /** Hoàn tất / ký xác nhận một lần đến */
  signVisit: (date: string) => void;
  sell: () => void;
  upload: (opts?: { cardId?: string; usageId?: string }) => void;
  openCard: (cardId: string) => void;
  openOrder: (orderId: string) => void;
}

export const TIER_STYLE: Record<Tier, string> = {
  'Thường': 'bg-slate-100 text-slate-600 border-slate-200',
  'Bạc': 'bg-gradient-to-r from-slate-100 to-slate-200 text-slate-700 border-slate-300',
  'Vàng': 'bg-gradient-to-r from-amber-50 to-yellow-100 text-amber-700 border-amber-300',
  'Kim cương': 'bg-gradient-to-r from-sky-50 to-indigo-100 text-indigo-700 border-indigo-300',
};

export const TierBadge = ({ tier, manual, size = 'sm' }: { tier: Tier; manual?: boolean; size?: 'sm' | 'lg' }) => (
  <span title={manual ? 'Phân loại tay' : 'Tự động theo chi tiêu'}
    className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border font-semibold ${TIER_STYLE[tier]} ${size === 'lg' ? 'px-3 py-1 text-sm' : 'px-2 py-0.5 text-xs'}`}>
    <Crown className={size === 'lg' ? 'h-4 w-4' : 'h-3 w-3'} /> {tier}{manual ? ' *' : ''}
  </span>
);

export const SegmentBadge = ({ segment, manual }: { segment: SegmentKey; manual?: boolean }) => (
  <span title={SEGMENT[segment].advice} className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-semibold ${SEGMENT[segment].cls}`}>
    {SEGMENT[segment].label}{manual ? ' *' : ''}
  </span>
);

export function Section({ title, extra, children, className = '' }: { title: React.ReactNode; extra?: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <section className={`rounded-xl border border-slate-200 bg-white ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-2.5">
        <h3 className="text-[15px] font-semibold text-slate-800">{title}</h3>
        {extra && <div className="flex flex-wrap items-center gap-2">{extra}</div>}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

export const Btn = ({ children, onClick, primary, disabled, title, danger }: { children: React.ReactNode; onClick?: () => void; primary?: boolean; disabled?: boolean; title?: string; danger?: boolean }) => (
  <button type="button" onClick={onClick} disabled={disabled} title={title}
    className={`inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-40 ${
      primary ? 'bg-[#357f96] text-white hover:bg-[#2c6b7f]'
        : danger ? 'border border-red-200 text-red-600 hover:bg-red-50'
          : 'border border-slate-300 text-slate-700 hover:border-[#3e91ab] hover:text-[#2c6b7f]'}`}>
    {children}
  </button>
);

/** Hồ sơ bán hàng (POS / thẻ) gắn với khách CMS: khớp SĐT tự động, hoặc liên kết tay khi khách đổi số */
export function LinkBar({ c }: { c: C360 }) {
  const [open, setOpen] = useState(false);
  const unlink = useC360Store(s => s.unlink);
  const { can, userName } = useTreatmentAuth();
  return (
    <>
      {c.pos.length ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          <Link2 className="h-4 w-4 text-[#3e91ab]" />
          <span>Hồ sơ bán hàng:</span>
          {c.pos.map(p => {
            const manual = c.manualIds.includes(p.id);
            return (
              <span key={p.id} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5">
                <b className="text-slate-700">{p.name}</b> {formatPhone(p.phone)}
                <span className="text-slate-400">· {manual ? 'liên kết tay' : 'khớp SĐT'}</span>
                {manual && can('edit') && (
                  <Tooltip title="Bỏ liên kết"><button onClick={() => unlink(c.key, p.id, userName)} className="text-slate-400 hover:text-red-600"><Unlink className="h-3.5 w-3.5" /></button></Tooltip>
                )}
              </span>
            );
          })}
          {can('edit') && <button onClick={() => setOpen(true)} className="ml-auto font-semibold text-[#2c6b7f] hover:underline">+ Liên kết thêm</button>}
          <DemoButton c={c} />
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <Link2 className="h-4 w-4" />
          <span className="min-w-0 flex-1">Chưa có hóa đơn / thẻ liệu trình nào khớp SĐT <b>{formatPhone(c.party.phone) || '—'}</b>. Hồ sơ bán hàng sẽ được tạo khi bán thẻ đầu tiên; khách đã mua bằng số khác thì liên kết tay.</span>
          {can('edit') && <button onClick={() => setOpen(true)} className="font-semibold text-[#2c6b7f] hover:underline">Liên kết hồ sơ có sẵn</button>}
          <DemoButton c={c} />
        </div>
      )}
      <LinkModal open={open} c={c} onClose={() => setOpen(false)} />
    </>
  );
}

/** Bản dev: nạp / xóa dữ liệu demo cho khách đang xem để xem đủ các tab 360 */
function DemoButton({ c }: { c: C360 }) {
  const has = useC360Store(s => !!s.demo[c.key]);
  const { userName } = useTreatmentAuth();
  const [busy, setBusy] = useState(false);
  if (!DEMO_ENABLED) return null;
  const run = async (fn: () => Promise<void>, ok: string) => {
    setBusy(true);
    try { await fn(); message.success(ok); } catch (e) { message.error('Không tạo được dữ liệu demo'); console.error(e); } finally { setBusy(false); }
  };
  return has ? (
    <button disabled={busy} onClick={() => Modal.confirm({
      title: 'Xóa dữ liệu demo của khách này?', content: 'Xóa ảnh, văn bản ký, phiếu lần đến, đánh giá do demo tạo và trả lại liên kết như trước.', okText: 'Xóa demo', okButtonProps: { danger: true }, cancelText: 'Không',
      onOk: () => run(() => clearDemo360(c.key, userName), 'Đã xóa dữ liệu demo'),
    })} className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 font-semibold text-violet-700 hover:bg-violet-100 disabled:opacity-50">
      <Sparkles className="h-3.5 w-3.5" /> {busy ? 'Đang xóa…' : 'Xóa dữ liệu demo'}
    </button>
  ) : (
    <button disabled={busy} onClick={() => run(() => seedDemo360(c.key, c.party, c.posIds, userName), 'Đã tạo dữ liệu demo: ảnh trước/sau, chữ ký, lần đến, đánh giá')}
      title="Chỉ có ở bản dev: sinh ảnh minh họa, văn bản đã ký, phiếu lần đến, đánh giá cho khách này"
      className="inline-flex items-center gap-1 rounded-full bg-violet-600 px-2.5 py-0.5 font-semibold text-white hover:bg-violet-700 disabled:opacity-50">
      <Sparkles className="h-3.5 w-3.5" /> {busy ? 'Đang tạo…' : 'Tạo dữ liệu demo'}
    </button>
  );
}

const normalize = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();

function LinkModal({ open, c, onClose }: { open: boolean; c: C360; onClose: () => void }) {
  const customers = usePosStore(s => s.customers);
  const orders = usePosStore(s => s.orders);
  const link = useC360Store(s => s.link);
  const { userName } = useTreatmentAuth();
  const [q, setQ] = useState('');
  const list = useMemo(() => {
    const nq = normalize(q.trim());
    const digits = q.replace(/\D/g, '');
    return customers
      .filter(x => !c.posIds.includes(x.id))
      .filter(x => !nq || (digits.length >= 3 ? x.phone.includes(digits) : normalize(x.name).includes(nq)))
      .slice(0, 30);
  }, [customers, q, c.posIds]);
  return (
    <Modal open={open} onCancel={onClose} footer={null} title="Liên kết hồ sơ bán hàng" centered width={560} destroyOnHidden>
      <p className="mb-2 text-xs text-slate-500">Gộp hóa đơn POS và thẻ liệu trình của hồ sơ được chọn vào Hồ sơ 360 của <b>{c.party.name}</b>. Dùng khi khách mua bằng SĐT khác / người nhà mua hộ.</p>
      <Input prefix={<Search className="h-4 w-4 text-slate-400" />} placeholder="Tìm tên hoặc SĐT" value={q} onChange={e => setQ(e.target.value)} allowClear autoFocus />
      <div className="mt-2 max-h-80 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200">
        {list.map(x => (
          <div key={x.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
            <div className="min-w-0">
              <div className="font-semibold">{x.name} <span className="font-normal text-slate-500">{formatPhone(x.phone)}</span></div>
              <div className="text-xs text-slate-400">{x.id} · {orders.filter(o => o.customerId === x.id).length} hóa đơn</div>
            </div>
            <Btn primary onClick={() => { link(c.key, x.id, userName); onClose(); }}>Liên kết</Btn>
          </div>
        ))}
        {!list.length && <div className="p-4 text-center text-sm text-slate-400">Không tìm thấy</div>}
      </div>
    </Modal>
  );
}
